import { useState } from 'react';
import { UserIcon, PhoneIcon, EnvelopeIcon, CalendarDaysIcon, WrenchScrewdriverIcon, HashtagIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { servicesData } from '../data/servicesData';
import SEO from '../components/SEO';
import { reportConversion } from '../utils/gtag';
import { useTranslation } from 'react-i18next';

const initial = {
  name: '',
  phone: '',
  email: '',
  make: '',
  model: '',
  year: '',
  mileage: '',
  services: [],
  preferredDate: '',
  notes: ''
};

export default function RequestQuote() {
  const { t } = useTranslation();
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorBanner, setErrorBanner] = useState('');

  function validate(values) {
    const e = {};
    if (!values.name.trim()) e.name = t('quote.form.errorRequired');
    if (!values.phone.trim()) e.phone = t('quote.form.errorRequired');
    if (!values.email.trim()) e.email = t('quote.form.errorRequired');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) e.email = t('quote.form.errorInvalid');
    if (!values.make.trim()) e.make = t('quote.form.errorRequired');
    if (!values.model.trim()) e.model = t('quote.form.errorRequired');
    if (!values.year.trim()) e.year = t('quote.form.errorRequired');
    if (!values.services || values.services.length === 0) e.service = t('quote.form.errorRequired');
    return e;
  }

  function handleChange(ev) {
    const { name, value, multiple, selectedOptions } = ev.target;
    if (name === 'services' && multiple) {
      const selected = Array.from(selectedOptions).map(o => o.value);
      setForm(f => ({ ...f, services: selected }));
    } else {
      setForm(f => ({ ...f, [name]: value }));
    }
  }

  async function handleSubmit(ev) {
    ev.preventDefault();
    setErrorBanner('');
    const v = validate(form);
    setErrors(v);
    if (Object.keys(v).length) return;
    try {
      setSubmitting(true);
      const subject = `New Quote Request from ${form.name} - ${form.make} ${form.model}`.trim();
      const formData = new FormData();
      formData.append('access_key', 'dc7eecc0-cd5e-4b79-8d0c-adf90cc4cc0a');
      formData.append('subject', subject);
  formData.append('from_name', form.name);
  formData.append('name', form.name); // compatibility alias
  formData.append('from_email', form.email);
  formData.append('email', form.email); // compatibility alias
      formData.append('phone', form.phone);
      formData.append('make', form.make);
      formData.append('model', form.model);
      formData.append('year', form.year);
    formData.append('mileage', form.mileage);
    // Send selected services as a comma-separated string (and include single 'service' alias for compatibility)
    const servicesStr = (form.services || []).join(', ');
    formData.append('services', servicesStr);
    formData.append('service', servicesStr);
      formData.append('preferredDate', form.preferredDate);
  formData.append('notes', form.notes);

      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      const data = await res.json();
      if (data.success) {
        setSubmitted(true);
        setForm(initial);
      } else {
        throw new Error(data.message || 'Submission failed');
      }
    } catch (err) {
      console.error(err);
      setErrorBanner(`${err.message || 'Submission failed.'} If this keeps happening, try fewer/smaller images or contact us via WhatsApp/call.`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <SEO
        title="Request Quote - Car Repair Ras Al Khor Dubai | Free Estimate - Vigor Auto"
        description="Get free car service quote from Vigor Auto Ras Al Khor Dubai. Transparent pricing, certified technicians, dealer-alternative workshop. European, Japanese, Korean & American cars. Serving Nad Al Hamar, Al Awir & International City."
        keywords="car repair quote dubai, auto service estimate Ras Al Khor, car service quote dubai, car garage quote Ras Al Khor, car repair estimate dubai, auto repair pricing Ras Al Khor, free car service quote, car maintenance cost dubai, transparent car repair pricing"
      />
      <div className="bg-gray-50">
        {/* Header */}
  <section className="relative overflow-hidden bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 text-white py-12 md:py-20">
          <div className="pointer-events-none absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_70%_30%,#F5D313,transparent_60%)]" />
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <p className="uppercase tracking-widest text-xs font-semibold text-[#F5D313]/80">{t('quote.badge')}</p>
            <h1 className="mt-4 text-4xl md:text-5xl font-extrabold tracking-tight leading-tight">{t('quote.title')}</h1>
            <p className="mt-6 text-slate-300 text-base md:text-lg leading-relaxed max-w-3xl">{t('quote.subtitle')}</p>
            <div className="mt-8 flex flex-wrap gap-4">
                <a href="https://wa.me/971524333690?text=I%20would%20like%20a%20service%20quote" target="_blank" rel="noopener noreferrer" onClick={() => reportConversion('https://wa.me/971524333690?text=I%20would%20like%20a%20service%20quote')} className="inline-flex rounded-lg bg-[#F5D313] px-6 py-3 text-sm font-semibold text-slate-900 shadow hover:bg-[#e4c40f] transition">{t('quote.whatsapp')}</a>
                <a href="tel:+971568699800" onClick={() => reportConversion('tel:+971568699800')} className="inline-flex rounded-lg border border-white/20 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10 transition">{t('common.callUs')}</a>
            </div>
          </div>
        </section>

        {/* Request Form */}
  <main className="py-10 md:py-16 pb-20 md:pb-28">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-12">
            <form onSubmit={handleSubmit} noValidate className="lg:col-span-2 bg-white border border-slate-200 shadow-xl shadow-slate-200/40 rounded-2xl p-6 sm:p-8 space-y-8 relative overflow-hidden">
              <div className="absolute -top-8 -right-8 w-48 h-48 bg-[#F5D313]/10 rounded-full" />
              <div className="relative">
                <h2 className="text-xl font-bold text-slate-900 tracking-tight">{t('quote.form.customerDetails')}</h2>
                <div className="mt-5 grid md:grid-cols-2 gap-6">
                  <div className="flex flex-col text-sm">
                    <label className="font-medium text-slate-700 mb-1">{t('quote.form.fullName')} *</label>
                    <div className="relative">
                      <UserIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                      <input name="name" value={form.name} onChange={handleChange} className={`w-full rounded-xl pl-10 pr-3 py-2.5 border ${errors.name ? 'border-red-400' : 'border-slate-200'} focus:ring-2 focus:ring-[#F5D313] focus:border-[#F5D313] text-sm bg-white/70 backdrop-blur shadow-sm`} placeholder="e.g. Ahmed" />
                    </div>
                    {errors.name && <span className="text-xs text-red-500 mt-1">{errors.name}</span>}
                  </div>
                  <div className="flex flex-col text-sm">
                    <label className="font-medium text-slate-700 mb-1">{t('quote.form.phone')} *</label>
                    <div className="relative">
                      <PhoneIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                      <input name="phone" value={form.phone} onChange={handleChange} className={`w-full rounded-xl pl-10 pr-3 py-2.5 border ${errors.phone ? 'border-red-400' : 'border-slate-200'} focus:ring-2 focus:ring-[#F5D313] focus:border-[#F5D313] text-sm bg-white/70 backdrop-blur shadow-sm`} placeholder="+971 5X ..." />
                    </div>
                    {errors.phone && <span className="text-xs text-red-500 mt-1">{errors.phone}</span>}
                  </div>
                  <div className="flex flex-col text-sm">
                    <label className="font-medium text-slate-700 mb-1">{t('quote.form.email')} *</label>
                    <div className="relative">
                      <EnvelopeIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                      <input name="email" type="email" value={form.email} onChange={handleChange} className={`w-full rounded-xl pl-10 pr-3 py-2.5 border ${errors.email ? 'border-red-400' : 'border-slate-200'} focus:ring-2 focus:ring-[#F5D313] focus:border-[#F5D313] text-sm bg-white/70 backdrop-blur shadow-sm`} placeholder="you@example.com" />
                    </div>
                    {errors.email && <span className="text-xs text-red-500 mt-1">{errors.email}</span>}
                  </div>
                  <div className="flex flex-col text-sm">
                    <label className="font-medium text-slate-700 mb-1">{t('quote.form.preferredDate')}</label>
                    <div className="relative">
                      <CalendarDaysIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                      <input name="preferredDate" type="date" value={form.preferredDate} onChange={handleChange} className="w-full rounded-xl pl-10 pr-3 py-2.5 border border-slate-200 focus:ring-2 focus:ring-[#F5D313] focus:border-[#F5D313] text-sm bg-white/70 backdrop-blur shadow-sm" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative">
                <h2 className="text-xl font-bold text-slate-900 tracking-tight">{t('quote.form.vehicleDetails')}</h2>
                <div className="mt-5 grid md:grid-cols-3 gap-6 text-sm">
                  <div className="flex flex-col">
                    <label className="font-medium text-slate-700 mb-1">{t('quote.form.make')} *</label>
                    <div className="relative">
                      <WrenchScrewdriverIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                      <input name="make" value={form.make} onChange={handleChange} className={`w-full rounded-xl pl-10 pr-3 py-2.5 border ${errors.make ? 'border-red-400' : 'border-slate-200'} focus:ring-2 focus:ring-[#F5D313] focus:border-[#F5D313] text-sm bg-white/70 backdrop-blur shadow-sm`} placeholder="e.g. Toyota" />
                    </div>
                    {errors.make && <span className="text-xs text-red-500 mt-1">{errors.make}</span>}
                  </div>
                  <div className="flex flex-col">
                    <label className="font-medium text-slate-700 mb-1">{t('quote.form.model')} *</label>
                    <div className="relative">
                      <WrenchScrewdriverIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                      <input name="model" value={form.model} onChange={handleChange} className={`w-full rounded-xl pl-10 pr-3 py-2.5 border ${errors.model ? 'border-red-400' : 'border-slate-200'} focus:ring-2 focus:ring-[#F5D313] focus:border-[#F5D313] text-sm bg-white/70 backdrop-blur shadow-sm`} placeholder="e.g. Camry" />
                    </div>
                    {errors.model && <span className="text-xs text-red-500 mt-1">{errors.model}</span>}
                  </div>
                  <div className="flex flex-col">
                    <label className="font-medium text-slate-700 mb-1">{t('quote.form.year')} *</label>
                    <div className="relative">
                      <HashtagIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                      <input name="year" value={form.year} onChange={handleChange} className={`w-full rounded-xl pl-10 pr-3 py-2.5 border ${errors.year ? 'border-red-400' : 'border-slate-200'} focus:ring-2 focus:ring-[#F5D313] focus:border-[#F5D313] text-sm bg-white/70 backdrop-blur shadow-sm`} placeholder="2022" />
                    </div>
                    {errors.year && <span className="text-xs text-red-500 mt-1">{errors.year}</span>}
                  </div>
                  <div className="flex flex-col">
                    <label className="font-medium text-slate-700 mb-1">{t('quote.form.mileage')}</label>
                    <div className="relative">
                      <HashtagIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                      <input name="mileage" value={form.mileage} onChange={handleChange} className="w-full rounded-xl pl-10 pr-3 py-2.5 border border-slate-200 focus:ring-2 focus:ring-[#F5D313] focus:border-[#F5D313] text-sm bg-white/70 backdrop-blur shadow-sm" placeholder="e.g. 120000" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative">
                <h2 className="text-xl font-bold text-slate-900 tracking-tight">{t('quote.form.serviceRequest')}</h2>
                <div className="mt-5 grid md:grid-cols-2 gap-6 text-sm">
                  <div className="flex flex-col">
                    <label className="font-medium text-slate-700 mb-1">{t('quote.form.serviceNeeded')} *</label>
                    <div className="relative">
                      <WrenchScrewdriverIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                      <select
                        name="services"
                        multiple
                        value={form.services}
                        onChange={handleChange}
                        className={`w-full rounded-xl pl-10 pr-3 py-2.5 border ${errors.service ? 'border-red-400' : 'border-slate-200'} focus:ring-2 focus:ring-[#F5D313] focus:border-[#F5D313] text-sm bg-white/70 backdrop-blur shadow-sm`}
                        aria-label="Select one or more services"
                      >
                        {servicesData.map(s => (
                          <option key={s.slug} value={s.title}>{s.title}</option>
                        ))}
                      </select>
                    </div>
                    {errors.service && <span className="text-xs text-red-500 mt-1">{errors.service}</span>}
                    <p className="mt-1 text-[11px] text-slate-500">{t('quote.form.multipleServices')}</p>
                  </div>
                  
                  <div className="flex flex-col md:col-span-2">
                    <label className="font-medium text-slate-700 mb-1">{t('quote.form.notes')}</label>
                    <div className="relative">
                      <ChatBubbleLeftRightIcon className="pointer-events-none absolute left-3 top-3 h-5 w-5 text-slate-400" />
                      <textarea name="notes" rows={4} value={form.notes} onChange={handleChange} className="w-full rounded-xl pl-10 pr-3 py-2.5 border border-slate-200 focus:ring-2 focus:ring-[#F5D313] focus:border-[#F5D313] text-sm bg-white/70 backdrop-blur shadow-sm resize-y" placeholder={t('quote.form.notesPlaceholder')} />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between flex-wrap gap-4 pt-2">
                <p className="text-xs text-slate-500">{t('quote.form.required')}</p>
                <button type="submit" disabled={submitting} className="inline-flex items-center justify-center rounded-lg bg-[#F5D313] px-8 py-3 text-sm font-bold text-slate-900 shadow-lg shadow-[#F5D313]/30 hover:bg-[#e4c40f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F5D313] disabled:opacity-60 disabled:cursor-not-allowed transition-all">
                  {submitting ? t('quote.form.submitting') : t('quote.form.submit')}
                </button>
              </div>
              {errorBanner && (
                <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                  {errorBanner}
                </div>
              )}
              {submitted && (
                <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">
                  <strong>{t('quote.form.success')}</strong>
                </div>
              )}
            </form>

            <aside className="space-y-8">
              <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm">
                <h3 className="font-semibold mb-3 text-slate-800 text-sm uppercase tracking-wide">{t('quote.sidebar.whyTitle')}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{t('quote.sidebar.whyText')}</p>
                <ul className="mt-5 space-y-2 text-sm text-slate-700">
                  <li className="flex gap-2"><span className="text-[#F5D313]">•</span> {t('quote.sidebar.benefit1')}</li>
                  <li className="flex gap-2"><span className="text-[#F5D313]">•</span> {t('quote.sidebar.benefit2')}</li>
                  <li className="flex gap-2"><span className="text-[#F5D313]">•</span> {t('quote.sidebar.benefit3')}</li>
                </ul>
              </div>
              <div className="p-6 rounded-2xl bg-blue-950 text-white relative overflow-hidden">
                <div className="pointer-events-none absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_30%_40%,#F5D313,transparent_70%)]" />
                <h3 className="font-semibold mb-3 text-sm uppercase tracking-wide text-[#F5D313]">{t('quote.sidebar.realTimeTitle')}</h3>
                <p className="text-slate-200 text-sm leading-relaxed mb-4">{t('quote.sidebar.realTimeText')}</p>
                <a
                  href="https://wa.me/971524333690?text=I%20am%20sending%20media%20for%20a%20quote"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => reportConversion('https://wa.me/971524333690?text=I%20am%20sending%20media%20for%20a%20quote')}
                  className="inline-flex w-full justify-center rounded-lg bg-[#F5D313] px-5 py-2.5 text-sm font-semibold text-slate-900 shadow hover:bg-[#e4c40f] transition"
                >
                  {t('quote.sidebar.openWhatsApp')}
                </a>
              </div>
            </aside>
          </div>
        </main>
      </div>
    </>
  );
}
